import {
    microsoftAuthApiRef,
    useApi
} from '@backstage/core-plugin-api';
import { useState, useCallback } from 'react'
import { useAsync } from 'react-use';
import * as jwt from 'jsonwebtoken';

export const useEntraOAuthSignIn = ({ scopes, sessionKey }: { scopes: string[], sessionKey: string }) => {
    if (scopes.length === 0) {
        throw new Error('Scopes are required');
    }

    const authApi = useApi(microsoftAuthApiRef);
    const [isSignedIn, setSignedIn] = useState(false);
    const [token, setToken] = useState('');
    const [forceLogin, setForceLogin] = useState(false);

    const createSessionValue = (token: string) => {
        return JSON.stringify({
            scopes: scopes,
            token: token,
        });
    }

    const doSignIn = useCallback(
        async (usePopup = true) => {
            const completeScopes = [...scopes, 'email', 'openid'];
            const accessToken = await authApi.getAccessToken(completeScopes, {
                optional: !usePopup,
                instantPopup: usePopup,
            });

            if (accessToken) {
                setSignedIn(!!accessToken);
                setToken(accessToken);
                sessionStorage.setItem(sessionKey, createSessionValue(accessToken));
            }
            
        },
        [authApi, setSignedIn, scopes, sessionKey]
    );

    const { loading, error } = useAsync(async () => {
        const sessionValue = sessionStorage.getItem(sessionKey);
        const value: { scopes: string[], token: string } | undefined = sessionValue ? JSON.parse(sessionValue) : undefined;
        if (value) {
            if (!scopes.every(scope => value.scopes.includes(scope))) {
                sessionStorage.removeItem(sessionKey);
                setToken('');
                setSignedIn(false);
                return;
            }

            const decodedToken = jwt.decode(value.token) as jwt.JwtPayload;
            const fiveMinutesInMs = 5 * 60 * 1000;
            if (decodedToken.exp && decodedToken.exp < (Date.now() - fiveMinutesInMs) / 1000) {
                sessionStorage.removeItem(sessionKey);
                setToken('');
                setSignedIn(false);
                return;
            }
            
            setToken(value.token);
            setSignedIn(true);
            return;
        }

        if (!isSignedIn) {
            await doSignIn(forceLogin);
        }
    }, [forceLogin, isSignedIn, sessionKey]);

    const showSignInModal = useCallback(() => {
        setForceLogin(true);
    }, [setForceLogin]);

    return { token, loading, error, showSignInModal, isSignedIn };
};
